// Types partagés du tableau de bord. Une seule forme de données, alimentée
// par l'endpoint consolidé GET /api/dashboard.

export type DashboardTab = 'overview' | 'profil' | 'history' | 'stats' | 'documents'

export type DocModule = 'transcription' | 'codification' | 'rapport' | 'note-technique'

export interface DashboardProfile {
  email: string | null
  full_name: string | null
  plan: string
  role: string | null
  is_admin: boolean
  credits: number
  created_at: string | null
}

export interface DashboardActivity {
  transcription: number
  codification: number
  note: number
  correction: number
  rapport: number
}

export interface DashboardDocument {
  id: string
  title: string
  statut: string | null
  word_count: number | null
  created_at: string
  content: string | null
  mode: string | null
}

export interface DashboardMovement {
  id: string
  type: string
  amount: number
  description: string | null
  created_at: string
}

export interface DashboardData {
  profile: DashboardProfile
  activity: DashboardActivity
  credits_added: number
  documents: Record<DocModule, DashboardDocument[]>
  movements: DashboardMovement[]
}

/**
 * Un PAIEMENT confirmé — servi par `GET /api/payment/history` (table
 * `transactions`).
 *
 * ⚠️ DÉLIBÉRÉMENT HORS de `DashboardData`, et distinct de `DashboardMovement`.
 * Les deux types ont un champ `amount`, mais ils ne mesurent pas la même chose :
 *   - `DashboardMovement.amount` = un nombre de CRÉDITS, sans devise ;
 *   - `DashboardPayment.amount`  = une somme d'ARGENT, dans `currency`.
 *
 * C'est exactement la confusion qui avait rendu l'historique des paiements
 * inopérant : il lisait `credit_transactions` en croyant y trouver des montants.
 * Les garder séparés rend le mélange impossible plutôt qu'improbable.
 */
export interface DashboardPayment {
  id: string
  payment_id: string
  amount: number
  currency: string
  provider: string | null
  method: string | null
  created_at: string
}

/** Somme d'argent, avec sa devise — jamais confondue avec un solde de crédits. */
export function formatMoney(amount: number, currency: string): string {
  return `${amount.toLocaleString('fr-FR')} ${currency}`
}

// Métadonnées d'affichage par module — partagées par toutes les sections
// pour garantir des libellés/couleurs cohérents.
export const MODULE_META: Record<
  DocModule,
  { label: string; emoji: string; color: string; activityKey: keyof DashboardActivity }
> = {
  transcription: { label: 'Transcription', emoji: '🎤', color: '#2d5a27', activityKey: 'transcription' },
  codification: { label: 'Codification', emoji: '🏷️', color: '#1B4332', activityKey: 'codification' },
  rapport: { label: "Rapport d'analyse", emoji: '🧠', color: '#40916c', activityKey: 'rapport' },
  'note-technique': { label: 'Note technique', emoji: '📋', color: '#52b788', activityKey: 'note' },
}

export const DOC_MODULE_ORDER: DocModule[] = [
  'note-technique',
  'transcription',
  'codification',
  'rapport',
]

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return '—'
  }
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

export function formatCredits(n: number): string {
  return `${(Math.round(n * 100) / 100).toLocaleString('fr-FR')} crédits`
}
